const TAG_HEADER_BYTES = 10;
const FRAME_HEADER_BYTES = 10;
const UNICODE_NULL_CHAR = '\u0000';
const SYNCH_SAFE_MASK = 0x7f;
const RECORD_FRAME_KEYS = ['PRIV', 'TXXX', 'WXXX'];
const TextEncoding = {
    Latin1: 0,
    UTF16: 1,
    UTF16BE: 2,
    UTF8: 3,
};
const DECODER_MAP = {
    [TextEncoding.Latin1]: new TextDecoder('iso-8859-1'),
    [TextEncoding.UTF16]: new TextDecoder('utf-16le'),
    [TextEncoding.UTF16BE]: new TextDecoder('utf-16be'),
    [TextEncoding.UTF8]: new TextDecoder('utf-8'),
};
const readSynchsafe = (v, startByte) => ((v.getUint8(startByte) & SYNCH_SAFE_MASK) << 21) |
    ((v.getUint8(startByte + 1) & SYNCH_SAFE_MASK) << 14) |
    ((v.getUint8(startByte + 2) & SYNCH_SAFE_MASK) << 7) |
    (v.getUint8(startByte + 3) & SYNCH_SAFE_MASK);
const readUInt32 = (v, off) => v.getUint32(off);
const ATTRIBUTE_LIST_REGEX = /([\w-]+)="((?:\\"|[^"])*)"/g;
function removeUnsync(src) {
    if (src.indexOf(0xff) === -1) {
        return src;
    }
    let readPos = 0;
    let writePos = 0;
    while (readPos < src.length) {
        if (readPos + 1 < src.length &&
            src[readPos] === 0xff &&
            src[readPos + 1] === 0x00) {
            src[writePos++] = 0xff;
            readPos += 2;
        }
        else {
            if (writePos !== readPos) {
                src[writePos] = src[readPos];
            }
            writePos++;
            readPos++;
        }
    }
    return src.subarray(0, writePos);
}
function decodeText(buf, enc) {
    if (enc === TextEncoding.UTF16) {
        if (buf[0] === 0xff && buf[1] === 0xfe) {
            return DECODER_MAP[TextEncoding.UTF16].decode(buf.subarray(2));
        }
        if (buf[0] === 0xfe && buf[1] === 0xff) {
            return DECODER_MAP[TextEncoding.UTF16BE].decode(buf.subarray(2));
        }
        return DECODER_MAP[TextEncoding.UTF16BE].decode(buf);
    }
    return (DECODER_MAP[enc] ?? DECODER_MAP[TextEncoding.Latin1]).decode(buf);
}
export function parseKeyValueString(str) {
    const out = {};
    if (!str) {
        return out;
    }
    ATTRIBUTE_LIST_REGEX.lastIndex = 0;
    let match = ATTRIBUTE_LIST_REGEX.exec(str);
    while (match) {
        out[match[1]] = match[2];
        match = ATTRIBUTE_LIST_REGEX.exec(str);
    }
    return out;
}
function parseStructuredText(text) {
    try {
        if (text.trim().startsWith('{')) {
            return JSON.parse(text);
        }
    }
    catch {
    }
    try {
        const kv = parseKeyValueString(text);
        return Object.keys(kv).length ? kv : undefined;
    }
    catch {
    }
    return undefined;
}
function parseFrame(out, id, payload, id3MinorVersion, debug = false) {
    if (id[0] === 'T' && id !== 'TXXX') {
        const encoding = payload[0];
        let text = decodeText(payload.subarray(1), encoding);
        if (id3MinorVersion === 4) {
            text = text.split(UNICODE_NULL_CHAR)[0];
        }
        else {
            const term = text.indexOf(UNICODE_NULL_CHAR);
            if (term !== -1)
                text = text.slice(0, term);
        }
        out[id] = text;
        return;
    }
    if (id === 'TXXX') {
        const encoding = payload[0];
        const propertyEnd = payload.indexOf(0, 1);
        if (propertyEnd === -1) {
            return;
        }
        const property = decodeText(payload.subarray(1, propertyEnd), encoding);
        if (!property) {
            return;
        }
        const value = decodeText(payload.subarray(propertyEnd + 1), encoding).split(UNICODE_NULL_CHAR)[0];
        const parsed = parseStructuredText(value);
        out.TXXX[property] = parsed ?? value;
        return;
    }
    if (id === 'WXXX') {
        const encoding = payload[0];
        const propertyEnd = payload.indexOf(0, 1);
        if (propertyEnd === -1) {
            return;
        }
        const property = decodeText(payload.subarray(1, propertyEnd), encoding);
        if (!property) {
            return;
        }
        const value = decodeText(payload.subarray(propertyEnd + 1), TextEncoding.Latin1);
        const parsed = parseStructuredText(value);
        out.WXXX[property] = parsed ?? value;
        return;
    }
    if (id === 'PRIV') {
        const propertyEnd = payload.indexOf(0);
        if (propertyEnd === -1) {
            return;
        }
        const property = decodeText(payload.subarray(0, propertyEnd), TextEncoding.UTF8);
        if (!property) {
            return;
        }
        const value = payload.subarray(propertyEnd + 1);
        const decodedString = decodeText(value, TextEncoding.UTF8);
        const parsed = parseStructuredText(decodedString);
        if (parsed) {
            out.PRIV[property] = parsed;
            return;
        }
        if (debug) {
            out.PRIV[property] = decodedString ?? value;
            return;
        }
    }
}
function parseSingleTag(tag, debug = false) {
    const out = { WXXX: {}, PRIV: {}, TXXX: {} };
    if (tag.byteLength < TAG_HEADER_BYTES) {
        return { result: out, bytesRead: tag.byteLength };
    }
    const view = new DataView(tag.buffer, tag.byteOffset, tag.byteLength);
    const id3MinorVersion = tag[3];
    if (id3MinorVersion === 2) {
        return { result: out, bytesRead: TAG_HEADER_BYTES };
    }
    const flags = tag[5];
    const usesTagUnsync = !!(flags & 0x80);
    const hasExtendedHeader = !!(flags & 0x40);
    const hasFooter = id3MinorVersion === 4 && !!(flags & 0x10);
    const tagSize = readSynchsafe(view, 6);
    const bytesRead = TAG_HEADER_BYTES + tagSize;
    if (bytesRead > tag.byteLength) {
        return { result: out, bytesRead: tag.byteLength };
    }
    let offset = TAG_HEADER_BYTES;
    if (hasExtendedHeader) {
        if (id3MinorVersion === 4) {
            const extendedHeaderSize = readSynchsafe(view, offset);
            offset += extendedHeaderSize;
        }
        else {
            const extendedHeaderSize = readUInt32(view, offset);
            offset += 4 + extendedHeaderSize;
        }
    }
    const tagEnd = TAG_HEADER_BYTES + tagSize;
    let tagBody = tag.subarray(offset, tagEnd - (hasFooter ? TAG_HEADER_BYTES : 0));
    if (usesTagUnsync)
        tagBody = removeUnsync(tagBody);
    const bodyView = new DataView(tagBody.buffer, tagBody.byteOffset, tagBody.byteLength);
    const frameDataLen = tagBody.length;
    let ptr = 0;
    while (ptr + FRAME_HEADER_BYTES <= frameDataLen) {
        const id = String.fromCharCode(tagBody[ptr], tagBody[ptr + 1], tagBody[ptr + 2], tagBody[ptr + 3]);
        if (id === '\0\0\0\0')
            break;
        const frameSize = id3MinorVersion === 4
            ? readSynchsafe(bodyView, ptr + 4)
            : readUInt32(bodyView, ptr + 4);
        if (frameSize === 0)
            break;
        const frameFlags = bodyView.getUint16(ptr + 8);
        const hasFrameUnsync = id3MinorVersion === 4 && !!(frameFlags & 0x02);
        const payloadStart = ptr + FRAME_HEADER_BYTES;
        const payloadEnd = payloadStart + frameSize;
        if (payloadEnd > frameDataLen)
            break;
        let payload = tagBody.subarray(payloadStart, payloadEnd);
        if (!usesTagUnsync && hasFrameUnsync) {
            payload = removeUnsync(payload);
        }
        parseFrame(out, id, payload, id3MinorVersion, debug);
        ptr = payloadEnd;
    }
    return { result: out, bytesRead };
}
export const FrameID = {
    TITLE: 'TIT2',
    ARTIST: 'TPE1',
    ALBUM: 'TALB',
    USER_DEFINED_TEXT: 'TXXX',
    USER_DEFINED_URL: 'WXXX',
    PRIVATE: 'PRIV',
};
export function parseId3Tags(data, debug = false) {
    const finalResult = { WXXX: {}, PRIV: {}, TXXX: {} };
    let offset = 0;
    if (debug) {
        console.log('[DEBUG] Raw ID3 metadata', data);
        console.log(formatTagAsHex(data));
    }
    while (offset < data.length) {
        const currentBuffer = data.slice(offset);
        if (currentBuffer.length < TAG_HEADER_BYTES ||
            currentBuffer[0] !== 0x49 ||
            currentBuffer[1] !== 0x44 ||
            currentBuffer[2] !== 0x33) {
            break;
        }
        const { result, bytesRead } = parseSingleTag(currentBuffer, debug);
        for (const key of Object.keys(result)) {
            if (RECORD_FRAME_KEYS.includes(key)) {
                if (!finalResult[key]) {
                    finalResult[key] = {};
                }
                Object.assign(finalResult[key], result[key]);
            }
            else {
                finalResult[key] = result[key];
            }
        }
        offset += bytesRead;
    }
    return finalResult;
}
const utf8Encoder = new TextEncoder();
export function createFingerprint(id3) {
    const hexParts = [];
    const appendBytes = (bytes) => {
        for (let i = 0; i < bytes.length; i++) {
            hexParts.push(bytes[i].toString(16).padStart(2, '0'));
        }
    };
    const appendValueBytes = (value) => {
        if (value === undefined ||
            typeof value === 'function' ||
            typeof value === 'symbol') {
            return;
        }
        if (typeof value === 'string') {
            appendBytes(utf8Encoder.encode(value));
        }
        else if (value instanceof Uint8Array) {
            appendBytes(value);
        }
        else if (Array.isArray(value)) {
            for (const v of value)
                appendValueBytes(v);
        }
        else if (value && typeof value === 'object') {
            for (const k of Object.keys(value).sort()) {
                appendBytes(utf8Encoder.encode(k));
                appendValueBytes(value[k]);
            }
        }
        else {
            appendBytes(utf8Encoder.encode(String(value)));
        }
    };
    for (const key of Object.keys(id3).sort()) {
        appendBytes(utf8Encoder.encode(key));
        appendValueBytes(id3[key]);
    }
    return hexParts.join('');
}
function formatTagAsHex(tag) {
    const hexLines = [];
    const lineLength = 16;
    for (let i = 0; i < tag.length; i += lineLength) {
        const chunk = tag.subarray(i, i + lineLength);
        const hex = Array.from(chunk)
            .map((byte) => byte.toString(16).padStart(2, '0'))
            .join(' ');
        const ascii = Array.from(chunk)
            .map((byte) => byte >= 32 && byte <= 126 ? String.fromCharCode(byte) : '.')
            .join('');
        const offset = i.toString(16).padStart(8, '0');
        hexLines.push(`${offset}  ${hex.padEnd(lineLength * 3 - 1)}  |${ascii}|`);
    }
    return hexLines.join('\n');
}
